import React, { ReactElement, useContext, useEffect, useState } from 'react'
import { Context } from '@/components/Context'
import { ArtData } from '@/database/art'
import Button from '@/components/Button'
import { custodial, uni1d800 } from '@/utils/extras'
import { forSaleButtonText } from '@/utils/formatting'
import { ArtState } from '@/database/types'

const MAXIMUM_EXPIRATION = '168'
const MINIMUM_EXPIRATION = '12'

const Offer = ({ artData }: { artData: ArtData }): ReactElement => {
    const { accountSignature, tokenAmount, offerHandler, loading, setLoading } = useContext(Context)
    const [tokens, setTokens] = useState(parseFloat(tokenAmount === '-' ? '0' : tokenAmount))
    const [offerPower, setOfferPower] = useState('')
    const [offerValue, setOfferValue] = useState('')
    const [expirationRadio, setExpirationRadio] = useState('12')
    const [expiration, setExpiration] = useState('12')

    function onChangeRadio(event: { target: { value: React.SetStateAction<string> } }) {
        setExpirationRadio(event.target.value)
        setExpiration(MINIMUM_EXPIRATION)
    }

    const handleExpirationInputChange = (event: { target: { value: string } }) => {
        setExpirationRadio('other')
        const input = event.target.value.replace(',', '.') // Normalize input

        // Check if the last character is a dot or if the input is just a dot
        const endsWithDot = input.endsWith('.') || input === '.'

        // Use a regular expression to validate if the input is in a correct numeric format (including dots)
        const isValidNumericInput = /^(\d+\.?\d*|\.\d+)$/.test(input)

        if (endsWithDot || isValidNumericInput) {
            // Directly update the state with the input if it's a valid numeric input or ends with a dot
            const newInput = parseFloat(input)
            if (newInput > parseFloat(MAXIMUM_EXPIRATION)) {
                setExpiration(MAXIMUM_EXPIRATION)
            } else {
                setExpiration(input)
            }
        } else if (input === '') {
            setExpiration('')
        }
    }

    const handleBidInputChange = (event: { target: { value: string } }) => {
        const input = event.target.value.replace(',', '.') // Normalize input

        // Check if the last character is a dot or if the input is just a dot
        const endsWithDot = input.endsWith('.') || input === '.'

        // Use a regular expression to validate if the input is in a correct numeric format (including dots)
        const isValidNumericInput = /^(\d+\.?\d*|\.\d+)$/.test(input)

        if (endsWithDot || isValidNumericInput) {
            // Directly update the state with the input if it's a valid numeric input or ends with a dot
            const newInput = parseFloat(input)
            if (newInput > parseFloat(offerPower)) {
                setOfferValue(offerPower.toString())
            } else {
                setOfferValue(input)
            }
        } else if (input === '') {
            setOfferValue('')
        } else {
            setOfferValue(`0`)
        }
    }

    const validateExpiration = () => {
        const parsedExpiration = parseFloat(expiration)
        if (parsedExpiration < 12 || isNaN(parsedExpiration)) {
            setExpiration(MINIMUM_EXPIRATION.toString())
        } else if (parsedExpiration > parseFloat(MINIMUM_EXPIRATION)) {
            setExpiration(MAXIMUM_EXPIRATION.toString())
        }
        return true
    }

    useEffect(() => {
        if (artData.state === ArtState.FOR_SALE || artData.currentOwner === custodial) {
            if (artData.art.price < 0) {
                setOfferPower('')
            } else {
                setOfferPower(artData.art.price < tokens ? artData.art.price.toPrecision(4) : tokens.toPrecision(4))
            }
        } else {
            setOfferPower(tokenAmount === '-' ? '0' : tokenAmount)
        }
    }, [tokens, tokenAmount])

    useEffect(() => {
        setTokens(parseFloat(tokenAmount === '-' ? '0' : tokenAmount))
    }, [tokenAmount])

    useEffect(() => {
        setLoading(false)
    }, [])

    return (
        <div className="bid-expiration-container artwork-box">
            <div className="bid-container">
                <span className={uni1d800.className + ' gray'}>{'\uD836\uDE17CRAZY\u200C\uD836\uDE17'}</span>
                <input
                    type="range"
                    min="0"
                    max={parseFloat(offerPower) || 0}
                    step="0.01"
                    value={parseFloat(offerValue) || 0}
                    onChange={(event) => setOfferValue(parseFloat(event.target.value).toFixed(2))}
                />
                <input
                    type="text"
                    min="0"
                    max={parseFloat(offerPower) || 0}
                    value={offerValue}
                    onChange={handleBidInputChange}
                />
            </div>
            <div className="bid-container">
                {artData.currentOwner !== custodial && (
                    <React.Fragment>
                        <div className="gray">Offer Expiration</div>
                        <div className="expiration-section">
                            <input
                                id="12h"
                                type="radio"
                                value="12"
                                name="expiration"
                                checked={expirationRadio === '12'}
                                onChange={onChangeRadio}
                            />
                            <label htmlFor="12h">12h</label>
                            <input
                                id="other"
                                type="radio"
                                value="other"
                                name="expiration"
                                checked={expirationRadio === 'other'}
                                onChange={onChangeRadio}
                            />
                            <label htmlFor="other">other</label>
                        </div>
                        <div>
                            <input
                                type="text"
                                min="12"
                                max={MAXIMUM_EXPIRATION}
                                value={expiration}
                                onChange={handleExpirationInputChange}
                            />
                            <div className="gray">(12 - 168 hours)</div>
                        </div>
                    </React.Fragment>
                )}
                {artData.currentOwner !== custodial && (
                    <Button
                        btnName={offerPower !== offerValue ? `Your Offer` : `Buy Now`}
                        classStyles={'nav__medium '}
                        disabled={!accountSignature || !offerPower || loading}
                        handleClick={() =>
                            accountSignature
                                ? validateExpiration() &&
                                  offerHandler(artData, parseFloat(offerValue), parseFloat(expiration))
                                : alert('Please connect your wallet to proceed transaction')
                        }
                        cancelBg={undefined}
                    />
                )}
                {artData.art.state === ArtState.FOR_SALE && artData.currentOwner === custodial && (
                    <Button
                        btnName={forSaleButtonText(artData)}
                        classStyles={'nav__medium long-button'}
                        disabled={!accountSignature || (!offerPower && artData.currentOwner !== custodial) || loading}
                        handleClick={() =>
                            accountSignature
                                ? validateExpiration() && offerHandler(artData, artData.art.price, 0)
                                : alert('Please connect your wallet to proceed transaction')
                        }
                        cancelBg={undefined}
                    />
                )}
            </div>
        </div>
    )
}
export default Offer
